// components/leads/LeadDetailPipelinePanel.js
// Fase F1.2 · Pannello dettaglio pipeline-aware (call_stage + sales_stage).
// Visibile SOLO quando pipeline_split_enabled=true. Il backend resta autoritativo:
// la config UI rispecchia la state machine B1 ma 409/422/403 sono sempre finali.
import React, { useState, useMemo } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import {
  PhoneCall, PhoneForwarded, UserPlus, ArrowRightCircle, XCircle, CheckCircle,
  ArrowRight, ArrowLeftCircle, Handshake, Ban, Flag, Clock,
} from 'lucide-react';

// --- Config UI coerente con backend (helpers/pipeline_state_machine.py) ---
const CALL_LABELS = {
  contattato: 'Contattato', da_richiamare: 'Da Richiamare', da_assegnare: 'Da Assegnare',
  passato_alla_vendita: 'Passato alla Vendita', non_idoneo: 'Non Idoneo',
};
const SALES_LABELS = {
  assegnato: 'Nuovo Lead', appuntamento: 'Appuntamento', sopralluogo: 'Sopralluogo',
  preventivazione: 'Preventivazione', presentato: 'Presentato', trattativa: 'Trattativa',
  trattativa_lunga: 'Trattativa Lunga',
  contratto: 'Contratto', convertito: 'Convertito', perso: 'Perso',
};

// Transizioni ordinarie Call (handoff da_assegnare→passato_alla_vendita è a parte)
const CALL_TRANSITIONS = {
  contattato: ['da_richiamare', 'da_assegnare', 'non_idoneo'],
  da_richiamare: ['contattato', 'da_assegnare', 'non_idoneo'],
  da_assegnare: ['contattato', 'da_richiamare', 'non_idoneo'],
  passato_alla_vendita: [],
  non_idoneo: [],
};

const SALES_FORWARD = {
  assegnato: 'appuntamento', appuntamento: 'sopralluogo', sopralluogo: 'preventivazione',
  preventivazione: 'presentato', presentato: 'trattativa', trattativa: 'contratto',
  trattativa_lunga: 'contratto',
  contratto: 'convertito',
};
const SALES_ROLLBACK = {
  appuntamento: 'assegnato', sopralluogo: 'appuntamento', presentato: 'preventivazione',
  trattativa: 'presentato', trattativa_lunga: 'trattativa',
};
const SALES_TERMINAL = ['convertito', 'perso'];

const CALL_ORDER = ['contattato', 'da_richiamare', 'da_assegnare', 'passato_alla_vendita'];
const SALES_ORDER = ['assegnato', 'appuntamento', 'sopralluogo', 'preventivazione', 'presentato', 'trattativa', 'trattativa_lunga', 'contratto', 'convertito'];

const NON_ELIGIBLE_REASONS = [
  { value: 'non_target', label: 'Non target' },
  { value: 'zona_non_coperta', label: 'Zona non coperta' },
  { value: 'budget_incompatibile', label: 'Budget incompatibile' },
  { value: 'duplicato', label: 'Duplicato' },
  { value: 'dati_insufficienti', label: 'Dati insufficienti' },
  { value: 'non_raggiungibile', label: 'Non raggiungibile' },
  { value: 'altro', label: 'Altro' },
];

const errMsg = (e, fallback = 'Operazione non riuscita') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return fallback;
};

const StageTracker = ({ order, labels, current, doneLabel }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid="pipeline-tracker">
    {order.map((s, i) => {
      const idx = order.indexOf(current);
      const isCurrent = s === current;
      const isDone = idx >= 0 && i < idx;
      return (
        <React.Fragment key={s}>
          <span
            data-testid={`tracker-step-${s}`}
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              isCurrent ? 'bg-[#FF7819] text-white'
                : isDone ? 'bg-emerald-100 text-emerald-700'
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {labels[s]}
          </span>
          {i < order.length - 1 && <ArrowRight className="w-3 h-3 text-gray-300" />}
        </React.Fragment>
      );
    })}
  </div>
);

const LeadDetailPipelinePanel = ({ lead, technicians = [], assignableWorkUsers = [], transitions = [], canManageCall, canManageSales, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [nonEligibleOpen, setNonEligibleOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [persoOpen, setPersoOpen] = useState(false);

  const [handoffTech, setHandoffTech] = useState('');
  const [handoffPM, setHandoffPM] = useState('');
  const [handoffNotes, setHandoffNotes] = useState('');
  const [neReason, setNeReason] = useState('');
  const [neNote, setNeNote] = useState('');
  const [cbAt, setCbAt] = useState('');
  const [cbNote, setCbNote] = useState('');
  const [persoNote, setPersoNote] = useState('');

  const callStage = lead.call_stage;
  const salesStage = lead.sales_stage;
  const inSales = salesStage != null;
  const inCall = !inSales;
  const initialized = callStage != null || salesStage != null;

  const activeTechs = useMemo(
    () => (technicians || []).filter(t => t.role === 'external_technician' && t.is_active !== false),
    [technicians]
  );

  // HOTFIX · PM selezionabili: riuso /users/assignable?type=work filtrato a project_manager attivi
  const activePMs = useMemo(
    () => (assignableWorkUsers || []).filter(u => u.role === 'project_manager' && u.is_active !== false),
    [assignableWorkUsers]
  );

  // F2.4/P3 · Motivo Trattativa Lunga: source of truth = ultima lead_transition con
  // to_sales_stage === 'trattativa_lunga'. Nessuna duplicazione lato Lead, nessun nuovo campo.
  const trattativaLungaTransition = useMemo(() => {
    if (salesStage !== 'trattativa_lunga') return null;
    const matches = (transitions || []).filter(t => t.to_sales_stage === 'trattativa_lunga');
    if (matches.length === 0) return null;
    return matches.reduce((a, b) =>
      (new Date(b.created_at || 0) >= new Date(a.created_at || 0) ? b : a)
    );
  }, [transitions, salesStage]);

  const reload = () => onChanged && onChanged();

  const runCallTransition = async (toStage, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/call-transition`, { to_stage: toStage, ...extra });
      toast.success(`Stato Chiamata aggiornato: ${CALL_LABELS[toStage]}`);
      reload();
      return true;
    } catch (e) {
      toast.error(errMsg(e, 'Transizione Chiamata non riuscita'));
      return false;
    } finally { setBusy(false); }
  };

  const runSalesTransition = async (toStage, notes = null) => {
    // Blocco B · queste due transizioni sono appointment-driven: nessuna transizione
    // diretta dal dettaglio (niente bypass del workflow appuntamento sul Kanban).
    if ((salesStage === 'assegnato' && toStage === 'appuntamento') ||
        (salesStage === 'preventivazione' && toStage === 'presentato')) {
      toast.info(
        toStage === 'appuntamento'
          ? 'Per avanzare ad Appuntamento fissa un Sopralluogo dal Kanban Vendita.'
          : 'Per avanzare a Presentato fissa la Presentazione preventivo dal Kanban Vendita.'
      );
      return false;
    }
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/sales-transition`, { to_stage: toStage, notes });
      toast.success(`Stato Vendita aggiornato: ${SALES_LABELS[toStage]}`);
      reload();
      return true;
    } catch (e) {
      toast.error(errMsg(e, 'Transizione Vendita non riuscita'));
      return false;
    } finally { setBusy(false); }
  };

  // ---- Call handlers ----
  const handleCallTarget = (toStage) => {
    if (toStage === 'da_richiamare') { setCallbackOpen(true); return; }
    if (toStage === 'non_idoneo') { setNonEligibleOpen(true); return; }
    runCallTransition(toStage);
  };

  const submitCallback = async () => {
    if (!cbAt) { toast.error('Seleziona data e ora del richiamo'); return; }
    const iso = new Date(cbAt).toISOString();
    const ok = await runCallTransition('da_richiamare', { callback_at: iso, callback_note: cbNote || null });
    if (ok) { setCallbackOpen(false); setCbAt(''); setCbNote(''); }
  };

  const submitNonEligible = async () => {
    if (!neReason) { toast.error('Seleziona un motivo'); return; }
    if (neReason === 'altro' && !neNote.trim()) { toast.error('Nota obbligatoria quando il motivo è "Altro"'); return; }
    const ok = await runCallTransition('non_idoneo', {
      non_eligible_reason: neReason,
      non_eligible_note: neNote || null,
    });
    if (ok) { setNonEligibleOpen(false); setNeReason(''); setNeNote(''); }
  };

  const submitHandoff = async () => {
    if (!handoffTech && !handoffPM) { toast.error('Seleziona un tecnico oppure un Project Manager'); return; }
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/handoff`, { technician_id: handoffTech || null, project_manager_id: handoffPM || null, notes: handoffNotes || null });
      toast.success('Handoff completato: lead passato alla Vendita');
      setHandoffOpen(false); setHandoffTech(''); setHandoffPM(''); setHandoffNotes('');
      reload();
    } catch (e) {
      toast.error(errMsg(e, 'Handoff non riuscito'));
    } finally { setBusy(false); }
  };

  const submitPerso = async () => {
    const ok = await runSalesTransition('perso', persoNote || null);
    if (ok) { setPersoOpen(false); setPersoNote(''); }
  };

  // ---- Rendering ----
  if (!initialized) {
    return (
      <Card className="card-abitalo" data-testid="pipeline-panel-not-initialized">
        <CardHeader className="pb-4"><CardTitle className="text-h4">Pipeline Lead</CardTitle></CardHeader>
        <CardContent>
          <div className="p-3 bg-[#FFF9E5] rounded-lg border border-[#FFE999] text-sm text-[#B38C00]">
            Questo lead non è inizializzato nel modello dual-pipeline (nessuno stage Chiamata/Vendita).
          </div>
        </CardContent>
      </Card>
    );
  }

  const salesForward = SALES_FORWARD[salesStage];
  const salesRollback = SALES_ROLLBACK[salesStage];
  const salesIsTerminal = SALES_TERMINAL.includes(salesStage);
  const callTargets = CALL_TRANSITIONS[callStage] || [];

  return (
    <>
      <Card className="card-abitalo" data-testid="lead-pipeline-panel">
        <CardHeader className="pb-4">
          <CardTitle className="text-h4 flex items-center gap-2">
            {inSales ? <Handshake className="w-5 h-5 text-[#FF7819]" /> : <PhoneCall className="w-5 h-5 text-[#FF7819]" />}
            {inSales ? 'Pipeline Vendita' : 'Pipeline Chiamata'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stato corrente */}
          <div className="space-y-2">
            {inSales ? (
              <>
                <div className="flex items-center gap-2 text-sm" data-testid="pipeline-call-summary">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="text-[#737373]">Chiamata:</span>
                  <span className="font-medium text-emerald-700">Passato alla vendita</span>
                </div>
                <div className="flex items-center gap-2 text-sm" data-testid="pipeline-sales-current">
                  <span className="text-[#737373]">Vendita:</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#FF7819] text-white">
                    {SALES_LABELS[salesStage]}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm" data-testid="pipeline-call-current">
                <span className="text-[#737373]">Stato Chiamata:</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  callStage === 'non_idoneo' ? 'bg-rose-600 text-white' : 'bg-[#FF7819] text-white'
                }`}>
                  {CALL_LABELS[callStage]}
                </span>
              </div>
            )}
          </div>

          {/* Tracker */}
          {inSales ? (
            <StageTracker order={SALES_ORDER} labels={SALES_LABELS} current={salesStage} />
          ) : (
            <StageTracker order={CALL_ORDER} labels={CALL_LABELS} current={callStage} />
          )}

          {/* Motivo Trattativa Lunga (P3) · SOLO quando sales_stage == trattativa_lunga */}
          {inSales && salesStage === 'trattativa_lunga' && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-900" data-testid="trattativa-lunga-info">
              <div className="font-medium flex items-center gap-1"><Clock className="w-4 h-4" /> Motivo Trattativa Lunga</div>
              <p className="mt-1 text-rose-800 whitespace-pre-wrap break-words">
                {(trattativaLungaTransition?.notes || '').trim() || 'Motivazione non disponibile'}
              </p>
              {trattativaLungaTransition && (
                <p className="mt-1.5 text-xs text-rose-700/70">
                  {trattativaLungaTransition.created_at && new Date(trattativaLungaTransition.created_at).toLocaleString('it-IT')}
                  {trattativaLungaTransition.user_name && ` · ${trattativaLungaTransition.user_name}`}
                </p>
              )}
            </div>
          )}

          {/* Motivo non idoneità */}
          {inCall && callStage === 'non_idoneo' && lead.non_eligible_reason && (            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800" data-testid="non-eligible-info">
              <div className="font-medium flex items-center gap-1"><Ban className="w-4 h-4" /> Non idoneo</div>
              <div className="mt-1">
                {NON_ELIGIBLE_REASONS.find(r => r.value === lead.non_eligible_reason)?.label || lead.non_eligible_reason}
                {lead.non_eligible_note && <p className="mt-1 text-rose-700/80">{lead.non_eligible_note}</p>}
              </div>
            </div>
          )}

          {/* === AZIONI CALL === */}
          {inCall && canManageCall && callStage !== 'passato_alla_vendita' && callStage !== 'non_idoneo' && (
            <div className="space-y-2 pt-2 border-t border-gray-100" data-testid="call-actions">
              <Label className="text-xs text-[#737373]">Azioni Chiamata</Label>
              <div className="flex flex-wrap gap-2">
                {callTargets.map((t) => (
                  <Button
                    key={t}
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => handleCallTarget(t)}
                    data-testid={`call-action-${t}`}
                  >
                    {t === 'da_richiamare' && <PhoneForwarded className="w-4 h-4 mr-1.5" />}
                    {t === 'contattato' && <PhoneCall className="w-4 h-4 mr-1.5" />}
                    {t === 'da_assegnare' && <UserPlus className="w-4 h-4 mr-1.5" />}
                    {t === 'non_idoneo' && <XCircle className="w-4 h-4 mr-1.5" />}
                    {CALL_LABELS[t]}
                  </Button>
                ))}
              </div>
              {callStage === 'da_assegnare' && (
                <Button
                  className="btn-primary w-full mt-1"
                  size="sm"
                  disabled={busy}
                  onClick={() => setHandoffOpen(true)}
                  data-testid="handoff-open-btn"
                >
                  <ArrowRightCircle className="w-4 h-4 mr-1.5" />
                  Passa alla Vendita (handoff)
                </Button>
              )}
            </div>
          )}

          {/* === AZIONI SALES === */}
          {inSales && canManageSales && !salesIsTerminal && (
            <div className="space-y-2 pt-2 border-t border-gray-100" data-testid="sales-actions">
              <Label className="text-xs text-[#737373]">Azioni Vendita</Label>
              <div className="flex flex-wrap gap-2">
                {salesForward && (
                  <Button
                    className="btn-primary"
                    size="sm"
                    disabled={busy}
                    onClick={() => runSalesTransition(salesForward)}
                    data-testid={`sales-forward-${salesForward}`}
                  >
                    <ArrowRight className="w-4 h-4 mr-1.5" />
                    {SALES_LABELS[salesForward]}
                  </Button>
                )}
                {salesRollback && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => runSalesTransition(salesRollback)}
                    data-testid={`sales-rollback-${salesRollback}`}
                  >
                    <ArrowLeftCircle className="w-4 h-4 mr-1.5" />
                    Indietro: {SALES_LABELS[salesRollback]}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  disabled={busy}
                  onClick={() => setPersoOpen(true)}
                  data-testid="sales-perso-btn"
                >
                  <Flag className="w-4 h-4 mr-1.5" />
                  Segna Perso
                </Button>
              </div>
            </div>
          )}

          {inSales && salesIsTerminal && (
            <div className="pt-2 border-t border-gray-100 text-sm text-[#737373]" data-testid="sales-terminal-info">
              Stato terminale: <span className="font-medium">{SALES_LABELS[salesStage]}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Handoff */}
      <Dialog open={handoffOpen} onOpenChange={setHandoffOpen}>
        <DialogContent className="sm:max-w-md" data-testid="handoff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Handshake className="w-5 h-5 text-[#FF7819]" /> Passa alla Vendita</DialogTitle>
            <DialogDescription>
              Assegna un tecnico: il lead passerà atomicamente a <strong>Passato alla Vendita</strong> (Chiamata) e <strong>Assegnato</strong> (Vendita).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tecnico <span className="text-xs text-[#999] font-normal">(opzionale se coincide con il Project Manager)</span></Label>
              <Select value={handoffTech || '__none'} onValueChange={(v) => setHandoffTech(v === '__none' ? '' : v)}>
                <SelectTrigger data-testid="handoff-tech-select"><SelectValue placeholder="Seleziona tecnico" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Nessuno (usa il Project Manager come tecnico)</SelectItem>
                  {activeTechs.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!handoffTech && handoffPM && (
                <p className="text-xs text-[#FF7819]" data-testid="handoff-pm-as-tech-hint">
                  Il Project Manager selezionato verrà assegnato anche come tecnico.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Project Manager <span className="text-xs text-[#999] font-normal">(opzionale)</span></Label>
              <Select value={handoffPM || '__none'} onValueChange={(v) => setHandoffPM(v === '__none' ? '' : v)}>
                <SelectTrigger data-testid="handoff-pm-select"><SelectValue placeholder="Nessun PM" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Nessun PM</SelectItem>
                  {activePMs.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea value={handoffNotes} onChange={(e) => setHandoffNotes(e.target.value)} rows={2} data-testid="handoff-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandoffOpen(false)} disabled={busy}>Annulla</Button>
            <Button className="btn-primary" onClick={submitHandoff} disabled={busy || (!handoffTech && !handoffPM)} data-testid="handoff-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma handoff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Non Idoneo */}
      <Dialog open={nonEligibleOpen} onOpenChange={setNonEligibleOpen}>
        <DialogContent className="sm:max-w-md" data-testid="non-eligible-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600"><XCircle className="w-5 h-5" /> Segna Non Idoneo</DialogTitle>
            <DialogDescription>Indica il motivo dell'esclusione dalla pipeline.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Select value={neReason} onValueChange={setNeReason}>
                <SelectTrigger data-testid="non-eligible-reason-select"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
                <SelectContent>
                  {NON_ELIGIBLE_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nota {neReason === 'altro' ? '*' : '(opzionale)'}</Label>
              <Textarea value={neNote} onChange={(e) => setNeNote(e.target.value)} rows={2} data-testid="non-eligible-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNonEligibleOpen(false)} disabled={busy}>Annulla</Button>
            <Button variant="destructive" onClick={submitNonEligible} disabled={busy} data-testid="non-eligible-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Callback (Da Richiamare) */}
      <Dialog open={callbackOpen} onOpenChange={setCallbackOpen}>
        <DialogContent className="sm:max-w-md" data-testid="callback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PhoneForwarded className="w-5 h-5 text-amber-600" /> Pianifica richiamo</DialogTitle>
            <DialogDescription>Imposta quando richiamare il lead (data futura obbligatoria).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data e ora richiamo *</Label>
              <Input type="datetime-local" value={cbAt} onChange={(e) => setCbAt(e.target.value)} data-testid="callback-at-input" />
            </div>
            <div className="space-y-2">
              <Label>Nota (opzionale)</Label>
              <Textarea value={cbNote} onChange={(e) => setCbNote(e.target.value)} rows={2} data-testid="callback-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallbackOpen(false)} disabled={busy}>Annulla</Button>
            <Button className="btn-primary" onClick={submitCallback} disabled={busy || !cbAt} data-testid="callback-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Perso (Sales) */}
      <Dialog open={persoOpen} onOpenChange={setPersoOpen}>
        <DialogContent className="sm:max-w-md" data-testid="sales-perso-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Flag className="w-5 h-5" /> Segna lead come Perso</DialogTitle>
            <DialogDescription>Il lead uscirà dalla pipeline Vendita attiva.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nota (opzionale)</Label>
            <Textarea value={persoNote} onChange={(e) => setPersoNote(e.target.value)} rows={2} data-testid="sales-perso-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPersoOpen(false)} disabled={busy}>Annulla</Button>
            <Button variant="destructive" onClick={submitPerso} disabled={busy} data-testid="sales-perso-confirm-btn">
              {busy ? 'Elaboro...' : 'Conferma Perso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeadDetailPipelinePanel;
